import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Tabs, Tab, Chip, Button,
  IconButton, TextField, InputAdornment, Select, MenuItem, FormControl,
  InputLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, LinearProgress, Tooltip, Alert, Badge, Divider, List, ListItem,
  ListItemIcon, ListItemText, Collapse, CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Security as SecurityIcon,
  BugReport as BugIcon,
  Terminal as TerminalIcon,
  Storage as StorageIcon,
  NetworkCheck as NetworkIcon,
  VpnKey as KeyIcon,
  FindInPage as FindIcon,
  Folder as FolderIcon,
  CloudDownload as DownloadIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Timeline as TimelineIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import api from '../../services/api/client';
import { useI18n } from '../../i18n';

// MITRE ATT&CK category colors
const CATEGORY_COLORS = {
  reconnaissance: '#2196F3',
  resource_development: '#9C27B0',
  initial_access: '#F44336',
  execution: '#E91E63',
  persistence: '#FF9800',
  privilege_escalation: '#FF5722',
  defense_evasion: '#795548',
  credential_access: '#607D8B',
  discovery: '#00BCD4',
  lateral_movement: '#009688',
  collection: '#4CAF50',
  command_and_control: '#8BC34A',
  exfiltration: '#CDDC39',
  impact: '#F44336'
};

const CRITICALITY_COLORS = {
  low: '#4CAF50',
  medium: '#FF9800',
  high: '#F44336',
  critical: '#9C27B0'
};

const STATUS_ICONS = {
  pending: <PendingIcon color="disabled" />,
  queued: <PendingIcon color="info" />,
  running: <CircularProgress size={20} />,
  paused: <PauseIcon color="warning" />,
  completed: <SuccessIcon color="success" />,
  failed: <ErrorIcon color="error" />,
  cancelled: <StopIcon color="disabled" />
};

// Tab Panel Component
function TabPanel({ children, value, index, ...props }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...props}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

// Module Card Component
function ModuleCard({ module, onExecute, onAddToChain }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);

  return (
    <Card
      sx={{
        height: '100%',
        border: `2px solid ${CATEGORY_COLORS[module.category] || '#ccc'}`,
        opacity: module.is_active ? 1 : 0.6
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ flex: 1 }}>
            {module.display_name}
          </Typography>
          {module.is_dangerous && (
            <Tooltip title={t('apt.dangerousModule')}>
              <WarningIcon color="error" fontSize="small" />
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          <Chip
            label={t(`apt.categories.${module.category}`, module.category.replace('_', ' '))}
            size="small"
            sx={{
              bgcolor: CATEGORY_COLORS[module.category],
              color: 'white',
              fontSize: '0.7rem'
            }}
          />
          <Chip
            label={t(`apt.criticalities.${module.criticality}`, module.criticality)}
            size="small"
            sx={{
              bgcolor: CRITICALITY_COLORS[module.criticality],
              color: 'white',
              fontSize: '0.7rem'
            }}
          />
          {module.mitre_technique_id && (
            <Chip
              label={module.mitre_technique_id}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{
          height: expanded ? 'auto' : 40,
          overflow: 'hidden',
          mb: 1
        }}>
          {module.description}
        </Typography>

        <Collapse in={expanded}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            <strong>{t('apt.requiredParams')}:</strong> {module.required_params?.join(', ') || t('common.none')}
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            <strong>{t('apt.executions')}:</strong> {module.execution_count} ({module.success_count} {t('apt.successful')})
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            <strong>{t('apt.avgTime')}:</strong> {module.avg_execution_time?.toFixed(1) || '?'}s
          </Typography>
        </Collapse>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Box>
            <Tooltip title={t('apt.addToChain')}>
              <IconButton size="small" onClick={() => onAddToChain(module)}>
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('common.execute')}>
              <IconButton
                size="small"
                color="primary"
                onClick={() => onExecute(module)}
                disabled={!module.is_active}
              >
                <PlayIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// Chain Builder Component
function ChainBuilder({ modules, onSave, onClose }) {
  const { t } = useI18n();
  const [chainName, setChainName] = useState('');
  const [chainDescription, setChainDescription] = useState('');
  const [selectedModules, setSelectedModules] = useState([]);
  const [aiOrchestrated, setAiOrchestrated] = useState(true);

  const handleAddModule = (module) => {
    if (!selectedModules.find(m => m.id === module.id)) {
      setSelectedModules([...selectedModules, module]);
    }
  };

  const handleRemoveModule = (moduleId) => {
    setSelectedModules(selectedModules.filter(m => m.id !== moduleId));
  };

  const handleMoveModule = (index, direction) => {
    const newModules = [...selectedModules];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < newModules.length) {
      [newModules[index], newModules[newIndex]] = [newModules[newIndex], newModules[index]];
      setSelectedModules(newModules);
    }
  };

  const handleSave = () => {
    if (chainName && selectedModules.length > 0) {
      onSave({
        name: chainName,
        description: chainDescription,
        module_ids: selectedModules.map(m => m.id),
        ai_orchestrated: aiOrchestrated
      });
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t('apt.createChain')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('apt.chainName')}
              value={chainName}
              onChange={(e) => setChainName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('common.description')}
              value={chainDescription}
              onChange={(e) => setChainDescription(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />

            <Typography variant="subtitle2" gutterBottom>
              {t('apt.selectedModules')} ({selectedModules.length}):
            </Typography>
            <Paper variant="outlined" sx={{ p: 1, minHeight: 200, maxHeight: 300, overflow: 'auto' }}>
              {selectedModules.length === 0 ? (
                <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  {t('apt.addModulesFromList')}
                </Typography>
              ) : (
                <List dense>
                  {selectedModules.map((module, index) => (
                    <ListItem
                      key={module.id}
                      secondaryAction={
                        <Box>
                          <IconButton size="small" onClick={() => handleMoveModule(index, -1)} disabled={index === 0}>
                            <ExpandLessIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleMoveModule(index, 1)} disabled={index === selectedModules.length - 1}>
                            <ExpandMoreIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleRemoveModule(module.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemIcon>
                        <Chip label={index + 1} size="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={module.display_name}
                        secondary={module.category}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              {t('apt.availableModules')}:
            </Typography>
            <Paper variant="outlined" sx={{ p: 1, maxHeight: 400, overflow: 'auto' }}>
              <List dense>
                {modules.map((module) => (
                  <ListItem
                    key={module.id}
                    button
                    onClick={() => handleAddModule(module)}
                    disabled={selectedModules.find(m => m.id === module.id)}
                  >
                    <ListItemIcon>
                      <Chip
                        size="small"
                        sx={{
                          bgcolor: CATEGORY_COLORS[module.category],
                          width: 10,
                          height: 10,
                          '& .MuiChip-label': { display: 'none' }
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={module.display_name}
                      secondary={`${module.category} - ${module.mitre_technique_id || t('common.notAvailable')}`}
                    />
                    <AddIcon color="action" />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!chainName || selectedModules.length === 0}
          sx={{ textTransform: 'none' }}
        >
          {t('apt.createChainButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Main Dashboard Component
export default function APTModulesDashboard() {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', severity: 'info' });

  // Data states
  const [modules, setModules] = useState([]);
  const [chains, setChains] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [stats, setStats] = useState(null);
  const [mitreTactics, setMitreTactics] = useState([]);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [criticalityFilter, setCriticalityFilter] = useState('');
  const [tacticFilter, setTacticFilter] = useState('');

  // Dialog states
  const [showChainBuilder, setShowChainBuilder] = useState(false);
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [executeConfig, setExecuteConfig] = useState({});

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [modulesRes, chainsRes, executionsRes, evidenceRes, statsRes, tacticsRes] = await Promise.all([
        api.get('/apt/modules'),
        api.get('/apt/chains'),
        api.get('/apt/executions', { params: { limit: 50 } }),
        api.get('/apt/evidence', { params: { limit: 50 } }),
        api.get('/apt/stats'),
        api.get('/apt/mitre/tactics')
      ]);

      setModules(modulesRes.data);
      setChains(chainsRes.data);
      setExecutions(executionsRes.data);
      setEvidence(evidenceRes.data);
      setStats(statsRes.data);
      setMitreTactics(tacticsRes.data);
    } catch (error) {
      // Don't show error message for 401 (authentication) errors - user will be redirected to login
      if (error.response?.status === 401) {
        console.log('Authentication required, redirecting to login...');
        return;
      }
      console.error('Error fetching APT data:', error);
      setMessage({ text: t('apt.loadError'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtered modules
  const filteredModules = modules.filter(module => {
    if (searchQuery && !module.display_name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !module.description?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (categoryFilter && module.category !== categoryFilter) return false;
    if (criticalityFilter && module.criticality !== criticalityFilter) return false;
    if (tacticFilter && module.mitre_tactic_id !== tacticFilter) return false;
    return true;
  });

  // Handlers
  const handleExecuteModule = async (module) => {
    setSelectedModule(module);
    setExecuteConfig({});
    setShowExecuteDialog(true);
  };

  const handleConfirmExecute = async () => {
    try {
      await api.post('/apt/execute/module', {
        module_id: selectedModule.id,
        config: executeConfig
      });
      setMessage({ text: t('apt.moduleLaunched').replace('{name}', selectedModule.display_name), severity: 'success' });
      setShowExecuteDialog(false);
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) return;
      console.error('Error executing module:', error);
      setMessage({ text: t('apt.moduleLaunchError'), severity: 'error' });
    }
  };

  const handleCreateChain = async (chainData) => {
    try {
      await api.post('/apt/chains', chainData);
      setMessage({ text: t('apt.chainCreated'), severity: 'success' });
      setShowChainBuilder(false);
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) return;
      console.error('Error creating chain:', error);
      setMessage({ text: t('apt.chainCreateError'), severity: 'error' });
    }
  };

  const handleExecuteChain = async (chainId) => {
    try {
      await api.post('/apt/execute/chain', { chain_id: chainId });
      setMessage({ text: t('apt.chainLaunched'), severity: 'success' });
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) return;
      console.error('Error executing chain:', error);
      setMessage({ text: t('apt.chainLaunchError'), severity: 'error' });
    }
  };

  const handlePauseChain = async (chainId) => {
    try {
      await api.post(`/apt/chains/${chainId}/pause`);
      setMessage({ text: t('apt.chainPaused'), severity: 'info' });
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) return;
      console.error('Error pausing chain:', error);
      setMessage({ text: t('common.error'), severity: 'error' });
    }
  };

  const handleCancelChain = async (chainId) => {
    try {
      await api.post(`/apt/chains/${chainId}/cancel`);
      setMessage({ text: t('apt.chainCancelled'), severity: 'warning' });
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) return;
      console.error('Error cancelling chain:', error);
      setMessage({ text: t('common.error'), severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="min-h-screen bg-brand-black p-6">
      <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto">
      {/* Message Alert */}
      {message.text && (
        <Alert
          severity={message.severity}
          onClose={() => setMessage({ text: '', severity: 'info' })}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      {/* Header Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <SecurityIcon color="primary" sx={{ fontSize: 40 }} />
              <Typography variant="h4">{stats?.total_modules || 0}</Typography>
              <Typography variant="body2" color="text.secondary">{t('apt.modulesApt')}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TimelineIcon color="secondary" sx={{ fontSize: 40 }} />
              <Typography variant="h4">{stats?.total_chains || 0}</Typography>
              <Typography variant="body2" color="text.secondary">{t('apt.chains')}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TerminalIcon color="success" sx={{ fontSize: 40 }} />
              <Typography variant="h4">{stats?.total_executions || 0}</Typography>
              <Typography variant="body2" color="text.secondary">{t('apt.executions')}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <FolderIcon color="warning" sx={{ fontSize: 40 }} />
              <Typography variant="h4">{stats?.total_evidence || 0}</Typography>
              <Typography variant="body2" color="text.secondary">{t('apt.evidence')}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label={t('apt.modulesApt')} icon={<SecurityIcon />} iconPosition="start" sx={{ textTransform: 'none' }} />
          <Tab label={t('apt.chains')} icon={<TimelineIcon />} iconPosition="start" sx={{ textTransform: 'none' }} />
          <Tab label={t('apt.executions')} icon={<TerminalIcon />} iconPosition="start" sx={{ textTransform: 'none' }} />
          <Tab label={t('apt.evidence')} icon={<FolderIcon />} iconPosition="start" sx={{ textTransform: 'none' }} />
          <Tab label={t('apt.mitreMatrix')} icon={<ShieldIcon />} iconPosition="start" sx={{ textTransform: 'none' }} />
        </Tabs>
      </Paper>

      {/* Modules Tab */}
      <TabPanel value={activeTab} index={0}>
        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={t('apt.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
            }}
            sx={{ minWidth: 250, flexShrink: 0, '& input::placeholder': { textOverflow: 'ellipsis', color: 'rgba(255, 255, 255, 0.6)', opacity: 1 } }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('apt.category')}</InputLabel>
            <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} label={t('apt.category')}>
              <MenuItem value="">{t('apt.allCategories')}</MenuItem>
              {Object.keys(CATEGORY_COLORS).map(cat => (
                <MenuItem key={cat} value={cat}>{t(`apt.categories.${cat}`, cat.replace('_', ' '))}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('apt.criticality')}</InputLabel>
            <Select value={criticalityFilter} onChange={(e) => setCriticalityFilter(e.target.value)} label={t('apt.criticality')}>
              <MenuItem value="">{t('apt.allCriticalities')}</MenuItem>
              <MenuItem value="low">{t('apt.criticalities.low')}</MenuItem>
              <MenuItem value="medium">{t('apt.criticalities.medium')}</MenuItem>
              <MenuItem value="high">{t('apt.criticalities.high')}</MenuItem>
              <MenuItem value="critical">{t('apt.criticalities.critical')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('apt.mitreTactic')}</InputLabel>
            <Select value={tacticFilter} onChange={(e) => setTacticFilter(e.target.value)} label={t('apt.mitreTactic')}>
              <MenuItem value="">{t('apt.allTactics')}</MenuItem>
              {mitreTactics.map(tactic => (
                <MenuItem key={tactic.id} value={tactic.id}>{tactic.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton onClick={fetchData}>
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* Modules Grid */}
        <Grid container spacing={2}>
          {filteredModules.map(module => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={module.id}>
              <ModuleCard
                module={module}
                onExecute={handleExecuteModule}
                onAddToChain={() => setShowChainBuilder(true)}
              />
            </Grid>
          ))}
        </Grid>

        {filteredModules.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {modules.length === 0
              ? t('apt.noModulesAvailable')
              : t('apt.noModulesFiltered')}
          </Alert>
        )}
      </TabPanel>

      {/* Chains Tab */}
      <TabPanel value={activeTab} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowChainBuilder(true)}
            sx={{ textTransform: 'none' }}
          >
            {t('apt.newChain')}
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('apt.chainStatus')}</TableCell>
                <TableCell>{t('apt.chainProgress')}</TableCell>
                <TableCell>{t('apt.chainModules')}</TableCell>
                <TableCell>{t('apt.chainCreatedAt')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chains.map(chain => (
                <TableRow key={chain.id}>
                  <TableCell>
                    <Typography fontWeight="bold">{chain.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {chain.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={STATUS_ICONS[chain.status]}
                      label={t(`apt.status.${chain.status}`, chain.status)}
                      size="small"
                      color={chain.status === 'completed' ? 'success' : chain.status === 'running' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(chain.completed_modules / chain.total_modules) * 100}
                        sx={{ flex: 1 }}
                      />
                      <Typography variant="caption">
                        {chain.completed_modules}/{chain.total_modules}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{chain.total_modules}</TableCell>
                  <TableCell>
                    {new Date(chain.created_at).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell align="right">
                    {chain.status === 'draft' || chain.status === 'ready' ? (
                      <Tooltip title={t('common.launch')}>
                        <IconButton color="primary" onClick={() => handleExecuteChain(chain.id)}>
                          <PlayIcon />
                        </IconButton>
                      </Tooltip>
                    ) : chain.status === 'running' ? (
                      <>
                        <Tooltip title={t('common.pause')}>
                          <IconButton color="warning" onClick={() => handlePauseChain(chain.id)}>
                            <PauseIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.cancel')}>
                          <IconButton color="error" onClick={() => handleCancelChain(chain.id)}>
                            <StopIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {chains.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('apt.noChains')}
          </Alert>
        )}
      </TabPanel>

      {/* Executions Tab */}
      <TabPanel value={activeTab} index={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('apt.module')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('apt.target')}</TableCell>
                <TableCell>{t('apt.duration')}</TableCell>
                <TableCell>{t('common.date')}</TableCell>
                <TableCell>{t('apt.result')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {executions.map(exec => (
                <TableRow key={exec.id}>
                  <TableCell>{exec.module_id?.slice(0, 8) || t('common.notAvailable')}</TableCell>
                  <TableCell>
                    <Chip
                      icon={STATUS_ICONS[exec.status]}
                      label={t(`apt.status.${exec.status}`, exec.status)}
                      size="small"
                      color={exec.status === 'completed' ? 'success' : exec.status === 'failed' ? 'error' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{exec.target || '-'}</TableCell>
                  <TableCell>{exec.duration_seconds?.toFixed(1)}s</TableCell>
                  <TableCell>
                    {new Date(exec.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {exec.exit_code !== null && (
                      <Chip
                        label={`${t('apt.exitCode')}: ${exec.exit_code}`}
                        size="small"
                        color={exec.exit_code === 0 ? 'success' : 'error'}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Evidence Tab */}
      <TabPanel value={activeTab} index={3}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('apt.title')}</TableCell>
                <TableCell>{t('common.type')}</TableCell>
                <TableCell>{t('apt.classification')}</TableCell>
                <TableCell>{t('apt.size')}</TableCell>
                <TableCell>{t('apt.validated')}</TableCell>
                <TableCell>{t('common.date')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {evidence.map(ev => (
                <TableRow key={ev.id}>
                  <TableCell>
                    <Typography fontWeight="bold">{ev.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {ev.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={ev.evidence_type} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ev.classification}
                      size="small"
                      color={ev.classification === 'confidential' ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {ev.file_size ? `${(ev.file_size / 1024).toFixed(1)} KB` : '-'}
                  </TableCell>
                  <TableCell>
                    {ev.is_validated ? (
                      <SuccessIcon color="success" />
                    ) : (
                      <PendingIcon color="disabled" />
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(ev.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('common.download')}>
                      <IconButton size="small">
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {evidence.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('apt.noEvidence')}
          </Alert>
        )}
      </TabPanel>

      {/* MITRE ATT&CK Tab */}
      <TabPanel value={activeTab} index={4}>
        <Grid container spacing={2}>
          {mitreTactics.map(tactic => (
            <Grid item xs={12} sm={6} md={4} key={tactic.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {tactic.name}
                  </Typography>
                  <Chip label={tactic.id} size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {tactic.description}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">
                    <strong>{tactic.module_count}</strong> {t('apt.modulesAvailable')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Chain Builder Dialog */}
      {showChainBuilder && (
        <ChainBuilder
          modules={modules}
          onSave={handleCreateChain}
          onClose={() => setShowChainBuilder(false)}
        />
      )}

      {/* Execute Module Dialog */}
      <Dialog open={showExecuteDialog} onClose={() => setShowExecuteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('common.execute')} {selectedModule?.display_name}
        </DialogTitle>
        <DialogContent>
          {selectedModule?.is_dangerous && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('apt.dangerWarning')}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" paragraph>
            {selectedModule?.description}
          </Typography>
          {selectedModule?.required_params?.map(param => (
            <TextField
              key={param}
              fullWidth
              label={param}
              margin="normal"
              size="small"
              value={executeConfig[param] || ''}
              onChange={(e) => setExecuteConfig({ ...executeConfig, [param]: e.target.value })}
              required
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExecuteDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmExecute}
            startIcon={<PlayIcon />}
          >
            {t('common.execute')}
          </Button>
        </DialogActions>
      </Dialog>
      </div>
    </div>
  );
}
